import { useEffect, useRef } from 'react'
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d'
import * as THREE from 'three'

interface Props {
  url: string
}

export default function GsplatViewer({ url }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<any>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const viewer = new GaussianSplats3D.Viewer({
      cameraUp: [0, -1, 0],
      initialCameraPosition: [0, 0, -3],
      initialCameraLookAt: [0, 0, 0],
      sphericalHarmonicsDegree: 2,
      rootElement: container,
      sharedMemoryForWorkers: false,
      gpuAcceleratedSort: true,
    })
    viewerRef.current = viewer

    viewer
      .addSplatScene(url, {
        showLoadingUI: false,
        progressiveLoad: true,
      })
      .then(() => {
        viewer.start()
        autoSpin()
      })
      .catch((e: any) => {
        console.error('Failed to load splat:', e)
      })

    let spinHandle = 0
    function autoSpin() {
      const camera = viewer.camera
      if (!camera) return
      const target = new THREE.Vector3(0, 0, 0)
      let theta = Math.PI
      const radius = 3
      function loop() {
        theta += 0.003
        camera.position.set(
          Math.sin(theta) * radius,
          0,
          Math.cos(theta) * radius,
        )
        camera.lookAt(target)
        spinHandle = requestAnimationFrame(loop)
      }
      loop()

      const stop = () => {
        cancelAnimationFrame(spinHandle)
        spinHandle = 0
        container?.removeEventListener('pointerdown', stop)
        container?.removeEventListener('wheel', stop)
      }
      container?.addEventListener('pointerdown', stop, { once: true })
      container?.addEventListener('wheel', stop, { once: true })
    }

    return () => {
      if (spinHandle) cancelAnimationFrame(spinHandle)
      try {
        viewer.dispose?.()
      } catch {}
      viewerRef.current = null
    }
  }, [url])

  return <div ref={containerRef} className="w-full h-full" />
}
